import type { CreateMultiOrderAPI } from '@hero/api';
import type { Order, OrderContractElement, VehicleTypes } from '@hero/schema';
import type { OrderDraftHydration } from '../order-draft/adapters/fromOrder';
import { fromOrderAgainSource } from '../order-draft/adapters/fromOrder';
import {
  buildCreateMultiOrderPayload,
  createInitialPublishOrderState,
  type PublishOrderPriceOverride,
  type PublishOrderState,
} from '../usePublishOrderStore';
import { resolveReorderOneTimePriceLock } from './resolveReorderOneTimePriceLock';

export type ReorderCreateOverrides = {
  vehicleTypes?: VehicleTypes[];
  vehicleCount?: number;
  dateRange?: { from: Date; to: Date };
  oneTimePrice?: CreateMultiOrderAPI['req']['routes'][number]['price'];
  sourceLeg?: OrderContractElement;
};

/**
 * Maps {@link OrderDraftHydration} into a {@link PublishOrderState}
 * slice suitable for {@link buildCreateMultiOrderPayload}.
 */
export function hydrationToPublishOrderState(
  hydration: OrderDraftHydration,
  overrides: ReorderCreateOverrides = {},
): PublishOrderState {
  const vehicleTypes =
    overrides.vehicleTypes ??
    hydration.load.vehicleTypes ??
    (hydration.load.vehicleType ? [hydration.load.vehicleType] : []);

  const vehicleCount =
    overrides.vehicleCount ?? hydration.destinationVehicleQuantities[0] ?? 1;

  const pickupDate = overrides.dateRange
    ? {
        from: overrides.dateRange.from.toISOString(),
        to: overrides.dateRange.to.toISOString(),
      }
    : hydration.pickupDate;

  const priceLock =
    overrides.oneTimePrice ??
    resolveReorderOneTimePriceLock(overrides.sourceLeg);

  const priceOverrides: Array<PublishOrderPriceOverride | undefined> =
    hydration.priceOverrides.map((_, idx) =>
      idx === 0 && priceLock
        ? {
            itemsOriginalSubtotal: priceLock.itemsOriginalSubtotal,
            currency: priceLock.currency ?? 'SAR',
            taxable: priceLock.taxable ?? true,
          }
        : undefined,
    );

  return {
    ...createInitialPublishOrderState(),
    customer: hydration.customer,
    isInternalShipment: hydration.isInternalShipment,
    allocationStrategy: 'manual',
    origin: hydration.origin,
    destinations: hydration.destinations,
    destinationVehicleQuantities: hydration.destinations.map((_, idx) =>
      idx === 0 ? vehicleCount : hydration.destinationVehicleQuantities[idx] ?? 1,
    ),
    pickupDate,
    load: {
      ...hydration.load,
      vehicleTypes,
      vehicleType: vehicleTypes[0],
    },
    instructions: hydration.instructions,
    priceOverrides,
  };
}

/**
 * Builds a reorder create payload through the canonical
 * {@link buildCreateMultiOrderPayload} — same wire shape as publish,
 * agent, and mobile create.
 */
export function buildReorderCreatePayload(
  order: Order,
  overrides: ReorderCreateOverrides = {},
): CreateMultiOrderAPI['req'] {
  const hydration = fromOrderAgainSource(order);
  const state = hydrationToPublishOrderState(hydration, overrides);
  return buildCreateMultiOrderPayload(state);
}
